<script lang="ts">
	import { includingDeviceData } from 'src/stores/features/inclusion';
	import { format } from 'svelte-i18n';
	import {
		buildManualDskParsedData,
		formatDskToDashed,
		isValidDSK,
	} from 'src/helpers/z_wave_qr_helpers';
	import { read_qr_code } from 'src/services/http';
	import { os } from 'src/stores';
	import { mobile } from 'src/services/mobile-rework';
	import {
		Alert,
		Button,
		CardGrid,
		Checkbox,
		Input,
		Loader,
		SelectCard,
		Text,
	} from '@Shelly/shelly-design-system';

	export let inclusionData; // bound by IncludeWizard

	void inclusionData;

	type EntryMode = 'camera' | 'dsk' | undefined;

	let entryMode: EntryMode;
	let dskInput = '';
	let hadParsedData = false;
	let suppressEntryReset = false;

	$: canUseCameraScan = mobile.isMobile;

	$: selectedSecurities = $includingDeviceData?.zWaveParsedData?.securityClasses;
	$: securityClasses = $includingDeviceData?.zWaveParsedData?.validateSecurityClasses;
	$: zWaveParsedData = $includingDeviceData?.zWaveParsedData;
	$: hasParsedData = typeof zWaveParsedData !== 'undefined';
	$: hasParseError = hasParsedData && Object.keys(zWaveParsedData).includes('error');
	$: showSecurityStep = hasParsedData && !hasParseError;
	$: dskIsValid = Boolean(isValidDSK(dskInput));

	const securityClassLabels = {
		'-2': 'zwave_security_temporary',
		'-1': 'zwave_security_none',
		0: 'zwave_security_unauthenticated',
		1: 'zwave_security_authenticated',
		2: 'zwave_security_access_control',
		7: 'zwave_security_legacy',
	};

	const getSecurityLabel = (index: number) => {
		const key = securityClassLabels[index];
		const defaults = {
			'-2': 'Temporary',
			'-1': 'None',
			0: 'Unauthenticated',
			1: 'Authenticated',
			2: 'Access control',
			7: 'Legacy',
		};
		return $format(key, { default: defaults[index] });
	};

	const validateCheckboxes = () => {
		if (typeof securityClasses === 'undefined') return;

		Object.values(securityClasses).forEach((security) => {
			if (
				!$includingDeviceData?.zWaveParsedData?.requestedSecurityClasses.includes(security.index)
			) {
				securityClasses[security.index].disabled = true;
			} else {
				securityClasses[security.index].disabled = false;
			}
			if (selectedSecurities.length === 1) {
				if (selectedSecurities.includes(security.index)) {
					securityClasses[security.index].disabled = true;
				}
			} else if (selectedSecurities.includes(security.index)) {
				securityClasses[security.index].disabled = false;
			}
		});
	};

	const selectSecurityOptions = (option: number) => {
		const _selectedSecurities = Array.from(selectedSecurities);
		if (_selectedSecurities.includes(option)) {
			_selectedSecurities.splice(_selectedSecurities.indexOf(option), 1);
		} else {
			_selectedSecurities.push(option);
		}
		selectedSecurities = _selectedSecurities;
		validateCheckboxes();

		includingDeviceData.updateData({
			zWaveParsedData: {
				...$includingDeviceData.zWaveParsedData,
				securityClasses: _selectedSecurities,
			},
		});
	};

	const startCameraScan = () => {
		if (!mobile.isMobile) return;
		suppressEntryReset = true;
		entryMode = 'camera';
		includingDeviceData.updateData({ zWaveParsedData: undefined });
		const scanDelayMs = $os === 'IOS' ? 400 : 0;
		setTimeout(() => {
			read_qr_code();
		}, scanDelayMs);
	};

	const startDskEntry = () => {
		suppressEntryReset = true;
		entryMode = 'dsk';
		dskInput = '';
		includingDeviceData.updateData({ zWaveParsedData: undefined });
	};

	const confirmDsk = () => {
		if (!dskIsValid) return;
		buildManualDskParsedData(dskInput);
	};

	const onDskInput = (event: Event) => {
		const target = event.target as HTMLInputElement;
		dskInput = formatDskToDashed(target.value);
	};

	$: ($includingDeviceData?.zWaveParsedData?.securityClasses?.length, () => validateCheckboxes());

	// Wizard "prev" clears parsed data — return to mode select (not when switching entry mode).
	$: {
		if (hadParsedData && !hasParsedData && !suppressEntryReset) {
			entryMode = undefined;
			dskInput = '';
		}
		suppressEntryReset = false;
		hadParsedData = hasParsedData;
	}
</script>

{#if showSecurityStep}
	<Text>
		{$format('zwave_security_classes_prompt', {
			default: 'Choose which security classes to grant the new node:',
		})}
	</Text>
	<Text class="mb-s">
		{$format('zwave_security_classes_hint', {
			default: 'At least one must be granted or the inclusion will be canceled.',
		})}
	</Text>
	{#each Object.values(securityClasses) as security (security.index)}
		<Checkbox
			id={'checkbox-security-' + security.index}
			aria-labelledby={'checkbox-security-' + security.index}
			on:click={() => selectSecurityOptions(security.index)}
			checked={selectedSecurities.includes(security.index)}
			disabled={security.disabled}>{getSecurityLabel(security.index)}</Checkbox
		>
	{/each}
{:else if hasParseError}
	<Alert
		heading={$format('label_info', { default: 'Info' })}
		message={$format('zwave_qr_scan_canceled', {
			default: 'QR scan was canceled or returned empty data. Go back and try again.',
		})}
	/>
	<Button
		class="mt-s"
		variant="secondary"
		aria-label={$format('zwave_switch_to_dsk', { default: 'Enter DSK instead' })}
		on:click={startDskEntry}
	>
		{$format('zwave_switch_to_dsk', { default: 'Enter DSK instead' })}
	</Button>
{:else if entryMode === 'camera'}
	<Loader />
{:else if entryMode === 'dsk'}
	<Text class="mb-s">
		{$format('zwave_dsk_input_hint', {
			default: '8 groups of 5 digits, with or without dashes',
		})}
	</Text>
	<Input
		id="zwave-dsk-input"
		aria-label={$format('zwave_dsk_input_label', { default: 'Device DSK' })}
		label={$format('zwave_dsk_input_label', { default: 'Device DSK' })}
		placeholder="#####-#####-#####-#####-#####-#####-#####-#####"
		value={dskInput}
		on:input={onDskInput}
		error={dskInput.length > 0 && !dskIsValid
			? $format('zwave_dsk_invalid', { default: 'Invalid DSK format' })
			: undefined}
		class="mb-s"
	/>
	<Button
		class="width-full"
		disabled={!dskIsValid}
		aria-label={$format('button_continue', { default: 'Continue' })}
		on:click={confirmDsk}
	>
		{$format('button_continue', { default: 'Continue' })}
	</Button>
{:else}
	<Text class="mb-s">
		{$format('zwave_entry_mode_prompt', {
			default: 'How do you want to identify the Wave device?',
		})}
	</Text>
	<CardGrid lg={290} sm={270} modalContent gridGap="s" noPaddingBottom>
		<div class="d-flex">
			<SelectCard
				type="horizontal"
				variant="radio"
				class="width-full"
				icon="fal fa-camera"
				title={$format('zwave_entry_camera', { default: 'Scan QR with camera' })}
				descriptiveText={$format('zwave_entry_camera_hint', {
					default: 'Use your phone camera to scan the SmartStart QR code on the device.',
				})}
				selected={false}
				disabled={!canUseCameraScan}
				on:click={startCameraScan}
			/>
		</div>
		<div class="d-flex">
			<SelectCard
				type="horizontal"
				variant="radio"
				class="width-full"
				icon="fal fa-keyboard"
				title={$format('zwave_entry_dsk', { default: 'Enter DSK manually' })}
				descriptiveText={$format('zwave_entry_dsk_hint', {
					default: 'Type the 40-digit DSK printed on the device or packaging.',
				})}
				selected={false}
				on:click={startDskEntry}
			/>
		</div>
	</CardGrid>
{/if}
